import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Image,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { MdLibraryBooks } from "react-icons/md";
import { useModeloMeta, useTemplateDeOrigem } from "@/hooks/useDisparos";
import {
  COMPONENTE_EXTERNO_LABEL,
  CatalogoDisparo,
  ComponenteExterno,
  ContextoDisparo,
  DisparoTemplateItem,
  MODO_TEMPLATE_LABEL,
  ModeloMeta,
  ModoTemplate,
  ProvedorComunicacao,
  TipoConteudo,
  canalAceitaAssunto,
  extrairVariaveis,
  variaveisMalformadas,
} from "@/models/consignado/disparo.model";
import {
  CanalDraft,
  IDIOMA_META,
  ParametroDraft,
  corpoEnviado,
  novoParametro,
  rotuloMensagemPropria,
} from "./disparo-draft";
import {
  camposExternosDoProvedor,
  limiteCaracteresDoProvedor,
  modosDoProvedor,
} from "./catalogo.helpers";
import RichTextEditor from "./RichTextEditor";
import VariaveisAvisos from "./VariaveisAvisos";
import { useVariavelInsert } from "./VariavelInsertContext";
import CampoLabel from "./CampoLabel";
import { fieldFont, labelStyles } from "./styleTokens";

type Props = {
  canal: CanalDraft;
  catalogo?: CatalogoDisparo;
  /** Contexto do disparo — traduz a propriedade da variável no rótulo do campo. */
  contexto: ContextoDisparo;
  onChange: (patch: Partial<CanalDraft>) => void;
  /** Insere um token no campo de texto ativo (ver VariavelInsertContext). */
  onInserirToken: (token: string) => void;
  /**
   * Mensagem vinculada a um template reutilizável: o backend a troca pela do
   * template ao salvar, então editar aqui seria descartado. Os campos ficam
   * desabilitados; para editar, desfaz-se o vínculo em "Origem da mensagem".
   */
  somenteLeitura?: boolean;
  /** Abre o template vinculado para edição (só no disparo — ver CanalCard). */
  onEditarTemplate?: (template: DisparoTemplateItem) => void;
};

/** Limite prático de um SMS; acima disso a operadora quebra em várias partes. */

// Textarea que se registra como alvo ativo da sidebar ao existir e ao focar.
// valueRef espelha `value` pra inserir na posição certa mesmo se o registro
// for de um foco anterior.
function TextoSimples({
  value,
  onChange,
  canalId,
}: {
  value: string;
  onChange: (v: string) => void;
  canalId: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const { registerActiveInsert } = useVariavelInsert();

  const insert = (token: string) => {
    const el = ref.current;
    const atual = valueRef.current;
    if (!el) {
      onChange(`${atual}${token}`);
      return;
    }
    const start = el.selectionStart ?? atual.length;
    const end = el.selectionEnd ?? atual.length;
    onChange(`${atual.slice(0, start)}${token}${atual.slice(end)}`);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  useEffect(() => {
    registerActiveInsert(canalId, insert);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerActiveInsert, canalId]);

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
      <Textarea
        ref={ref}
        {...fieldFont}
        fontSize="sm"
        rows={5}
        border="none"
        _focusVisible={{ boxShadow: "none" }}
        px={1}
        placeholder="Escreva a mensagem. Use a barra lateral para inserir variáveis."
        value={value}
        onFocus={() => registerActiveInsert(canalId, insert)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Box>
  );
}

// Assunto do e-mail. Registra-se como alvo da sidebar só no foco, não ao
// montar: o alvo padrão, antes de qualquer clique, continua sendo o corpo.
// Sem este registro, clicar num campo com o foco aqui inseria o token no
// corpo, e a única saída era digitar à mão um apelido já declarado lá.
function AssuntoInput({
  value,
  onChange,
  canalId,
}: {
  value: string;
  onChange: (v: string) => void;
  canalId: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const { registerActiveInsert } = useVariavelInsert();

  const insert = (token: string) => {
    const el = ref.current;
    const atual = valueRef.current;
    if (!el) {
      onChange(`${atual}${token}`);
      return;
    }
    const start = el.selectionStart ?? atual.length;
    const end = el.selectionEnd ?? atual.length;
    onChange(`${atual.slice(0, start)}${token}${atual.slice(end)}`);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Input
      ref={ref}
      {...fieldFont}
      placeholder="Ex.: Seu empréstimo foi pago"
      value={value}
      onFocus={() => registerActiveInsert(canalId, insert)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Parâmetro de template externo: um valor por posição, agrupado por
// componente. O índice não é digitado — o backend exige que seja contínuo a
// partir de zero por componente, então ele é derivado da ordem na hora de
// montar o DTO (ver templateParaDto).
function ParametroRow({
  parametro,
  posicao,
  vaga,
  canalId,
  somenteCorpo,
  focarAoMontar = false,
  onChange,
  onRemove,
}: {
  parametro: ParametroDraft;
  posicao: number;
  /** Campo vindo do modelo da Meta: rótulo fixo, sem trocar componente nem remover. */
  vaga?: VagaModelo;
  canalId: string;
  somenteCorpo: boolean;
  /** Linha recém-adicionada: vira o alvo da sidebar, e não a linha anterior. */
  focarAoMontar?: boolean;
  onChange: (patch: Partial<ParametroDraft>) => void;
  onRemove: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const valueRef = useRef(parametro.valor);
  valueRef.current = parametro.valor;
  const { registerActiveInsert } = useVariavelInsert();

  useEffect(() => {
    if (focarAoMontar) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const insert = (token: string) => {
    const el = ref.current;
    const atual = valueRef.current;
    if (!el) {
      onChange({ valor: `${atual}${token}` });
      return;
    }
    const start = el.selectionStart ?? atual.length;
    const end = el.selectionEnd ?? atual.length;
    onChange({ valor: `${atual.slice(0, start)}${token}${atual.slice(end)}` });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const campo = (
    <Input
      ref={ref}
      size="sm"
      {...fieldFont}
      placeholder={vaga?.ajuda ?? "Texto, variável, ou os dois — ex.: utm_source=whatsapp&utm_content=24h{{cpf}}"}
      value={parametro.valor}
      onFocus={() => registerActiveInsert(canalId, insert)}
      onChange={(e) => onChange({ valor: e.target.value })}
    />
  );

  if (vaga) {
    return (
      <Flex gap={2} align="center" mb={2} direction={{ base: "column", md: "row" }}>
        <Text
          fontSize="sm"
          fontWeight="600"
          color="purple.800"
          w={{ base: "100%", md: "240px" }}
          flexShrink={0}
          noOfLines={1}
          title={vaga.rotulo}
          {...fieldFont}
        >
          {vaga.rotulo}
        </Text>
        {campo}
      </Flex>
    );
  }

  return (
    <Flex gap={2} align="center" mb={2}>
      <Select
        size="sm"
        w="170px"
        flexShrink={0}
        {...fieldFont}
        value={parametro.componente}
        isDisabled={somenteCorpo}
        onChange={(e) => onChange({ componente: e.target.value as ComponenteExterno })}
      >
        {(Object.keys(COMPONENTE_EXTERNO_LABEL) as ComponenteExterno[]).map((c) => (
          <option key={c} value={c}>
            {COMPONENTE_EXTERNO_LABEL[c]}
          </option>
        ))}
      </Select>
      <Text fontSize="xs" color="gray.500" w="20px" textAlign="center" {...fieldFont}>
        {posicao}
      </Text>
      {campo}
      <IconButton
        aria-label="Remover parâmetro"
        icon={<DeleteIcon />}
        size="sm"
        variant="ghost"
        colorScheme="red"
        onClick={onRemove}
      />
    </Flex>
  );
}

type VagaModelo = {
  componente: ComponenteExterno;
  posicao: number;
  rotulo: string;
  ajuda: string;
  /** Valor que entra quando o campo está vazio: a cópia da mídia do modelo no nosso storage. */
  sugestao?: string;
};

const MIDIA_CABECALHO: Record<string, string> = {
  IMAGEM: "imagem",
  VIDEO: "vídeo",
  DOCUMENTO: "documento",
};

const descreverCabecalho = (cabecalho: ModeloMeta["cabecalho"]): string => {
  if (!cabecalho) return "sem cabeçalho";
  if (cabecalho.tipo === "TEXTO") {
    return cabecalho.variavel ? `texto com {{${cabecalho.variavel}}}` : `texto fixo ("${cabecalho.texto ?? ""}")`;
  }
  return MIDIA_CABECALHO[cabecalho.tipo] ?? "formato sem parâmetro";
};

// Um campo por variável do modelo, na ordem em que o backend monta o envio:
// cabeçalho, corpo e botões de link dinâmico.
const vagasDoModelo = (modelo: ModeloMeta): VagaModelo[] => {
  const vagas: VagaModelo[] = [];
  const cabecalho = modelo.cabecalho;
  if (cabecalho?.exigeParametro) {
    const midia = MIDIA_CABECALHO[cabecalho.tipo];
    vagas.push({
      componente: "CABECALHO_IMAGEM",
      posicao: 0,
      rotulo: midia ? `Cabeçalho · ${midia}` : `Cabeçalho · {{${cabecalho.variavel}}}`,
      ajuda: midia ? `Link público da ${midia}` : `Valor de {{${cabecalho.variavel}}} em "${cabecalho.texto}"`,
      sugestao: cabecalho.linkMidia ?? undefined,
    });
  }
  modelo.corpo.variaveis.forEach((variavel, posicao) =>
    vagas.push({
      componente: "CORPO",
      posicao,
      rotulo: `Corpo · {{${variavel}}}`,
      ajuda: `Valor de {{${variavel}}}: texto, variável ou os dois`,
    }),
  );
  modelo.botoes
    .filter((b) => b.exigeParametro)
    .forEach((botao, posicao) =>
      vagas.push({
        componente: "BOTAO_URL",
        posicao,
        rotulo: `Botão "${botao.texto}"`,
        ajuda: `Completa o link ${botao.url ?? ""}`,
      }),
    );
  return vagas;
};

// Encaixa o que já estava cadastrado nos campos do modelo, pela posição dentro
// de cada componente. O que o modelo não usa sai; o que falta nasce vazio,
// exceto o cabeçalho de mídia, que recebe o link da cópia no nosso storage.
const alinharAoModelo = (vagas: VagaModelo[], atuais: ParametroDraft[]): ParametroDraft[] =>
  vagas.map((vaga) => {
    const atual =
      atuais.filter((p) => p.componente === vaga.componente)[vaga.posicao] ?? novoParametro(vaga.componente);
    return vaga.sugestao && !atual.valor.trim() ? { ...atual, valor: vaga.sugestao } : atual;
  });

// Resumo do modelo aprovado na Meta: diz se as variáveis são por posição ou por
// nome e mostra o texto, para dar contexto aos campos abaixo. Com cabeçalho de
// imagem, mostra acima do texto a imagem do link que está no campo, que é a
// que vai no envio.
function ModeloMetaResumo({
  carregando,
  erro,
  modelo,
  imagemCabecalho,
}: {
  carregando: boolean;
  erro: unknown;
  modelo?: ModeloMeta;
  imagemCabecalho?: string | null;
}) {
  if (carregando) {
    return (
      <Text fontSize="xs" color="gray.500" {...fieldFont}>
        Consultando o modelo na Meta…
      </Text>
    );
  }
  if (erro) {
    const naoEncontrado = (erro as { response?: { status?: number } })?.response?.status === 404;
    return (
      <Box bg="orange.50" borderRadius="md" px={3} py={2}>
        <Text fontSize="xs" color="orange.700" {...fieldFont}>
          {naoEncontrado
            ? "Modelo não encontrado na conta do WhatsApp com esse nome e idioma. Confira o nome do template e o idioma."
            : "Não foi possível consultar o modelo na Meta agora. O envio confere o modelo de novo na hora de mandar."}
        </Text>
      </Box>
    );
  }
  if (!modelo) return null;

  const botoesDinamicos = modelo.botoes.filter((b) => b.exigeParametro).length;

  return (
    <Box bg="purple.50" borderRadius="md" px={3} py={2}>
      <Text fontSize="xs" fontWeight="600" color="purple.800" {...fieldFont}>
        Modelo na Meta · variáveis por {modelo.formatoVariaveis === "NOME" ? "nome" : "posição"} · cabeçalho{" "}
        {descreverCabecalho(modelo.cabecalho)} · {botoesDinamicos || "nenhum"} botão(ões) com link dinâmico
        {modelo.status && modelo.status !== "APPROVED" ? ` · situação ${modelo.status}` : ""}
      </Text>
      {imagemCabecalho && (
        <Image
          src={imagemCabecalho}
          alt="Imagem do cabeçalho"
          mt={2}
          w="100%"
          maxW="320px"
          borderRadius="md"
        />
      )}
      {modelo.corpo.texto && (
        <Text
          fontSize="xs"
          color="purple.900"
          whiteSpace="pre-wrap"
          maxH="140px"
          overflowY="auto"
          mt={1}
          {...fieldFont}
        >
          {modelo.corpo.texto}
        </Text>
      )}
    </Box>
  );
}

/**
 * Mensagem de um canal, nos dois modos do contrato:
 *
 * - CORPO_PROPRIO — o texto é escrito aqui. Assunto e HTML são exclusivos
 *   de e-mail; os outros canais mandam texto puro.
 * - REFERENCIA_EXTERNA — o texto já foi aprovado no provedor e aqui só se
 *   informa qual template é e o que entra em cada posição.
 *
 * Quais modos aparecem vem do catálogo de canais (GET /catalogo/canais): a
 * Vonex, por exemplo, só opera com template aprovado.
 */
export default function TemplateMensagemForm({
  canal,
  catalogo,
  contexto,
  onChange,
  onInserirToken,
  somenteLeitura = false,
  onEditarTemplate,
}: Props) {
  const modos = canal.provedor ? modosDoProvedor(catalogo, canal.canal, canal.provedor) : [];
  const camposExternos = canal.provedor
    ? camposExternosDoProvedor(catalogo, canal.canal, canal.provedor)
    : [];
  const aceitaAssunto = canalAceitaAssunto(canal.canal);
  const isVonex = canal.provedor === ProvedorComunicacao.VONEX;
  // O teto é do canal × provedor e conta o texto escrito: as chaves de
  // {{variavel}} entram na conta, não o valor que elas viram no envio.
  const limiteCaracteres = canal.provedor
    ? limiteCaracteresDoProvedor(catalogo, canal.canal, canal.provedor)
    : undefined;
  // Conta o que vai para o backend — no HTML, com tags, estilos e a moldura —,
  // que é o que ele confere contra o limite do provedor.
  const tamanhoEnviado = corpoEnviado(canal).length;
  const excedeuLimite = limiteCaracteres !== undefined && tamanhoEnviado > limiteCaracteres;

  // Se o provedor não suporta o modo atual (ex.: trocou e-mail/SendGrid por
  // WhatsApp/Vonex), cai no primeiro suportado em vez de deixar o draft num
  // estado que a API recusaria.
  useEffect(() => {
    if (modos.length > 0 && !modos.includes(canal.modo)) {
      onChange({ modo: modos[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modos.join(","), canal.modo]);

  const textos =
    canal.modo === ModoTemplate.CORPO_PROPRIO
      ? [canal.conteudo, canal.assunto]
      : canal.parametros.map((p) => p.valor);
  const usadas = [...new Set(textos.flatMap(extrairVariaveis))];
  const malformadas = [...new Set(textos.flatMap(variaveisMalformadas))];

  const updateParametro = (id: string, patch: Partial<ParametroDraft>) =>
    onChange({ parametros: canal.parametros.map((p) => (p.id === id ? { ...p, ...patch } : p)) });

  const removeParametro = (id: string) =>
    onChange({ parametros: canal.parametros.filter((p) => p.id !== id) });

  // A linha nova recebe o foco: sem isso a sidebar seguia inserindo na linha
  // anterior, e duas variáveis caíam na mesma posição.
  const [parametroNovo, setParametroNovo] = useState<string | null>(null);
  const adicionarParametro = () => {
    const novo = novoParametro();
    setParametroNovo(novo.id);
    onChange({ parametros: [...canal.parametros, novo] });
  };

  const templateOrigem = useTemplateDeOrigem(canal.templateId);

  const ehModeloMeta =
    canal.provedor === ProvedorComunicacao.META && canal.modo === ModoTemplate.REFERENCIA_EXTERNA;
  const [nomeModelo, setNomeModelo] = useState(canal.identificador.trim());
  useEffect(() => {
    const espera = setTimeout(() => setNomeModelo(canal.identificador.trim()), 600);
    return () => clearTimeout(espera);
  }, [canal.identificador]);
  const consultaModelo = useModeloMeta(nomeModelo, IDIOMA_META, ehModeloMeta);
  const modeloMeta = ehModeloMeta ? consultaModelo.data : undefined;
  const vagas = modeloMeta ? vagasDoModelo(modeloMeta) : [];
  const linkCabecalho =
    canal.parametros.find((p) => p.componente === "CABECALHO_IMAGEM")?.valor.trim() ?? "";
  const imagemCabecalho =
    modeloMeta?.cabecalho?.tipo === "IMAGEM" && /^https?:\/\//.test(linkCabecalho) ? linkCabecalho : null;

  // Com o modelo carregado, os parâmetros passam a ser os campos dele. Só reage
  // à troca de modelo: digitar num campo não muda a estrutura.
  useEffect(() => {
    if (!modeloMeta || somenteLeitura) return;
    const alinhados = alinharAoModelo(vagasDoModelo(modeloMeta), canal.parametros);
    const iguais =
      alinhados.length === canal.parametros.length && alinhados.every((p, i) => p === canal.parametros[i]);
    if (!iguais) onChange({ parametros: alinhados });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeloMeta, somenteLeitura]);

  // Posição exibida = índice dentro do componente, igual ao que vai no DTO.
  const posicaoDe = (parametro: ParametroDraft): number =>
    canal.parametros.filter((p) => p.componente === parametro.componente).indexOf(parametro);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Vínculo com template reutilizável. É vivo: ao salvar, o backend troca
          a mensagem do disparo pela do template, o detalhe devolve o template
          atual e o envio também o usa (DisparoConfiguracaoValidator,
          DisparoService.enriquecerCanal). Por isso a mensagem abaixo é só
          leitura enquanto o vínculo existir. */}
      {canal.templateId && (
        <Flex align="center" gap={2} bg="gray.50" borderRadius="md" px={3} py={2}>
          <Icon as={MdLibraryBooks} boxSize={3.5} color="gray.500" flexShrink={0} />
          <Text fontSize="xs" color="gray.600" {...fieldFont}>
            Mensagem do template{" "}
            <Text as="span" fontWeight="600">
              {templateOrigem.data?.nome ?? "reutilizável"}
            </Text>
            . Ela acompanha o template: o que for alterado nele vale para este disparo e para os
            outros que o usam. Para{" "}
            {canal.modo === ModoTemplate.REFERENCIA_EXTERNA
              ? "informar o template do provedor só neste disparo"
              : "escrever uma mensagem só deste disparo"}
            , escolha "{rotuloMensagemPropria(canal.modo)}" em Origem da mensagem.
          </Text>
          {onEditarTemplate && (
            <Button
              size="xs"
              variant="outline"
              colorScheme="green"
              flexShrink={0}
              isDisabled={!templateOrigem.data}
              onClick={() => templateOrigem.data && onEditarTemplate(templateOrigem.data)}
              {...fieldFont}
            >
              Editar template
            </Button>
          )}
        </Flex>
      )}

      {/* fieldset desabilitado desliga de uma vez todos os campos nativos da
          mensagem; o editor HTML (contenteditable) recebe isDisabled à parte. */}
      <Box as="fieldset" disabled={somenteLeitura} border="none" p={0} m={0} minW={0}>
      <VStack align="stretch" spacing={4}>

      {/* Com um modo só, o seletor não aparece: a tela já mostra os campos
          daquele modo, e não há escolha a fazer. */}
      {modos.length > 1 && (
        <FormControl>
          <CampoLabel ajuda="Escrever o texto aqui ou usar um template já aprovado no provedor.">Tipo de mensagem</CampoLabel>
          <Select
            {...fieldFont}
            value={canal.modo}
            onChange={(e) => onChange({ modo: e.target.value as ModoTemplate })}
          >
            {modos.map((m) => (
              <option key={m} value={m}>
                {MODO_TEMPLATE_LABEL[m]}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {canal.modo === ModoTemplate.CORPO_PROPRIO ? (
        <>
          {aceitaAssunto && (
            <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={4}>
              <GridItem>
                <FormControl isRequired>
                  <CampoLabel ajuda="Assunto do e-mail. Aceita variáveis.">Assunto</CampoLabel>
                  <AssuntoInput
                    canalId={canal.id}
                    value={canal.assunto}
                    onChange={(assunto) => onChange({ assunto })}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <CampoLabel ajuda="Texto puro ou HTML.">Formato</CampoLabel>
                  <Select
                    {...fieldFont}
                    value={canal.tipoConteudo}
                    onChange={(e) => onChange({ tipoConteudo: e.target.value as TipoConteudo })}
                  >
                    <option value="TEXTO">Texto simples</option>
                    <option value="HTML">HTML</option>
                  </Select>
                </FormControl>
              </GridItem>
            </Grid>
          )}

          <FormControl isRequired>
            <CampoLabel ajuda="Use a barra lateral para inserir variáveis.">Mensagem</CampoLabel>
            {aceitaAssunto && canal.tipoConteudo === "HTML" ? (
              <RichTextEditor
                isDisabled={somenteLeitura}
                canalId={canal.id}
                value={canal.conteudo}
                onChange={(conteudo) => onChange({ conteudo })}
              />
            ) : (
              <TextoSimples
                canalId={canal.id}
                value={canal.conteudo}
                onChange={(conteudo) => onChange({ conteudo })}
              />
            )}
            {limiteCaracteres !== undefined && (
              <Text
                {...fieldFont}
                mt={1}
                textAlign="right"
                color={excedeuLimite ? "red.500" : "gray.500"}
                fontWeight={excedeuLimite ? "600" : "400"}
              >
                {tamanhoEnviado} de {limiteCaracteres} caracteres
                {canal.tipoConteudo === "HTML" && aceitaAssunto && " (contando o HTML)"}
                {excedeuLimite && " — acima do limite deste canal"}
              </Text>
            )}
          </FormControl>
        </>
      ) : (
        <>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            <GridItem>
              <FormControl isRequired>
                <CampoLabel ajuda="O identificador do template aprovado no provedor.">Template no provedor</CampoLabel>
                <Input
                  {...fieldFont}
                  placeholder={isVonex ? "Ex.: 12345" : "Ex.: emprestimo_pago_tomador"}
                  value={canal.identificador}
                  onChange={(e) => onChange({ identificador: e.target.value })}
                />
              </FormControl>
            </GridItem>
            {camposExternos.includes("idioma") && (
              <GridItem>
                <FormControl>
                  <CampoLabel ajuda="Os modelos de WhatsApp da GranaTech são sempre em português do Brasil.">Idioma</CampoLabel>
                  <Input {...fieldFont} value={`Português (Brasil) · ${IDIOMA_META}`} isReadOnly isDisabled />
                </FormControl>
              </GridItem>
            )}
            {camposExternos.includes("produtoId") && (
              <GridItem>
                <FormControl isRequired>
                  <CampoLabel ajuda="Identificador do produto na Vonex.">ID do produto</CampoLabel>
                  <Input
                    {...fieldFont}
                    placeholder="Ex.: 67890"
                    value={canal.produtoId}
                    onChange={(e) => onChange({ produtoId: e.target.value })}
                  />
                </FormControl>
              </GridItem>
            )}
          </Grid>

          {ehModeloMeta && nomeModelo && (
            <ModeloMetaResumo
              carregando={consultaModelo.isLoading}
              erro={consultaModelo.error}
              modelo={modeloMeta}
              imagemCabecalho={imagemCabecalho}
            />
          )}

          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <FormLabel {...labelStyles} mb={0}>
                Parâmetros do template
              </FormLabel>
              {!modeloMeta && (
                <Button
                  size="sm"
                  leftIcon={<AddIcon />}
                  variant="outline"
                  colorScheme="green"
                  onClick={adicionarParametro}
                  {...fieldFont}
                >
                  Adicionar
                </Button>
              )}
            </Flex>

            {modeloMeta ? (
              vagas.length === 0 ? (
                <Text fontSize="xs" color="gray.500" {...fieldFont}>
                  Este modelo não tem variáveis: não há parâmetros a preencher.
                </Text>
              ) : (
                vagas.map((vaga) => {
                  const p = canal.parametros.filter((x) => x.componente === vaga.componente)[vaga.posicao];
                  if (!p) {
                    return (
                      <Flex key={`${vaga.componente}-${vaga.posicao}`} gap={2} mb={2} align="center">
                        <Text fontSize="sm" fontWeight="600" color="purple.800" w="240px" {...fieldFont}>
                          {vaga.rotulo}
                        </Text>
                        <Text fontSize="xs" color="gray.500" {...fieldFont}>
                          não preenchido
                        </Text>
                      </Flex>
                    );
                  }
                  return (
                    <ParametroRow
                      key={p.id}
                      parametro={p}
                      posicao={vaga.posicao}
                      vaga={vaga}
                      canalId={canal.id}
                      somenteCorpo={isVonex}
                      onChange={(patch) => updateParametro(p.id, patch)}
                      onRemove={() => removeParametro(p.id)}
                    />
                  );
                })
              )
            ) : canal.parametros.length === 0 ? (
              <Text fontSize="xs" color="gray.500" {...fieldFont}>
                Nenhum parâmetro. Adicione um para cada posição que o template aprovado espera.
                Cada um aceita texto, variáveis ou os dois juntos.
              </Text>
            ) : (
              canal.parametros.map((p) => (
                <ParametroRow
                  key={p.id}
                  parametro={p}
                  posicao={posicaoDe(p)}
                  canalId={canal.id}
                  somenteCorpo={isVonex}
                  focarAoMontar={p.id === parametroNovo}
                  onChange={(patch) => updateParametro(p.id, patch)}
                  onRemove={() => removeParametro(p.id)}
                />
              ))
            )}
          </Box>
        </>
      )}

      <Divider />

      <VariaveisAvisos
        variaveis={canal.variaveis}
        usadas={usadas}
        malformadas={malformadas}
        onInserir={(apelido) => onInserirToken(`{{${apelido}}}`)}
        catalogo={catalogo}
        contexto={contexto}
        // Mensagem vinculada a template é só leitura: o backend a troca pela do
        // template ao salvar, e um formato trocado aqui se perderia.
        onFormatoChange={
          somenteLeitura
            ? undefined
            : (variavelId, formato) =>
                onChange({
                  variaveis: canal.variaveis.map((v) =>
                    v.id === variavelId ? { ...v, formato } : v,
                  ),
                })
        }
      />
      </VStack>
      </Box>
    </VStack>
  );
}
